import {queryClient} from "../../react-query-client-provider/ReactQueryClientProvider";
import {WorkflowsService, WorkflowStatus} from "../../../services/WorkflowsService";

import {WORKFLOWS_QUERY_KEY} from "./useWorkflowsQuery";

/** Gap between checks of POST /api/v4/workflows/search. */
export const REFRESH_INTERVAL_MS = 1_000;

/** How many times to look before giving up. Giving up is a failure. */
export const REFRESH_ATTEMPTS = 10;

const timestamp = () => new Date().toISOString();

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Polls search until `admissionId` is returned with `expectedStatus`, then refreshes the board.
 *
 * Create and status changes write through Java. Search reads the replica, which trails that
 * write, so the first pages can still show the previous status. Stops at the first match.
 *
 * A draft patch does not change status, so that row is already in the result. Pass `updatedAt`,
 * the write's epoch seconds, and a row counts only once the replica's `updated` has reached it.
 * Matching on status alone refetches the pre-write card, and the save never shows.
 *
 * Ten misses is a timeout and leaves the board as it is.
 */
export const tryRefreshDashboard = async (
  admissionId: string,
  expectedStatus: WorkflowStatus,
  updatedAt?: number,
): Promise<void> => {
  console.log("[tryRefreshDashboard] start", {
    admissionId,
    status: expectedStatus,
    ...(updatedAt === undefined ? {} : {updatedAt}),
    timestamp: timestamp(),
  });

  for (let attempt = 1; attempt <= REFRESH_ATTEMPTS; attempt++) {
    console.log("[tryRefreshDashboard] request", {
      admissionId,
      status: expectedStatus,
      attempt,
      timestamp: timestamp(),
    });

    try {
      const response = await WorkflowsService.search({
        statuses: [expectedStatus],
        page: 0,
        pageSize: 10,
        starredOnly: false,
        sortBy: "updated",
        sortDirection: "DESC",
      });
      const found = response.data?.cards?.some(
        card =>
          card.id === admissionId &&
          card.status === expectedStatus &&
          // Without a floor, the status itself is the catch-up signal: the
          // pre-write row is not in this status yet. With one, a row already
          // at that status is the pre-write copy until its updated time moves.
          (updatedAt === undefined || card.updated >= updatedAt),
      );
      if (response.ok && found) {
        console.log("[tryRefreshDashboard] success", {
          admissionId,
          status: expectedStatus,
          attempt,
          timestamp: timestamp(),
        });
        await queryClient.refetchQueries([WORKFLOWS_QUERY_KEY]);
        return;
      }
    } catch {
      // A failed request is one miss. The timeout log is what records giving up.
    }

    if (attempt < REFRESH_ATTEMPTS) {
      await sleep(REFRESH_INTERVAL_MS);
    }
  }

  console.log("[tryRefreshDashboard] failure", {
    admissionId,
    status: expectedStatus,
    reason: "timeout",
    timestamp: timestamp(),
  });
};
